import { supabase } from '@/services/supabase/supabase';
import { User } from '@supabase/supabase-js';

/**
 * Interface for JWT claims
 */
interface JwtClaims {
  sub: string; // User ID
  email?: string;
  role: string;
  tenant_id?: string;
  iat: number;
  exp: number;
}

/**
 * Parse JWT token to extract claims
 * @param token - JWT token string
 * @returns Parsed JWT claims or null if invalid
 */
export function parseJwt(token: string): JwtClaims | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

/**
 * Get the current user ID from the session
 * @returns Promise with user ID or null
 */
export async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    
    // Try to get from JWT claims first (faster)
    const token = session.access_token;
    const claims = parseJwt(token);
    if (claims?.sub) {
      return claims.sub;
    }
    
    // Fallback to user object
    return session.user?.id || null;
  } catch (error) {
    console.error('Error getting current user ID:', error);
    return null;
  }
}

/**
 * Get the current tenant ID from the session
 * @returns Promise with tenant ID or null
 */
export async function getCurrentTenantId(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    
    // Try to get from JWT claims first (faster)
    const token = session.access_token;
    const claims = parseJwt(token);
    if (claims?.tenant_id) {
      return claims.tenant_id;
    }
    
    // If tenant_id is not in JWT, we'll need to fetch it
    // This should be avoided by ensuring tenant_id is included in JWT claims
    console.warn('Tenant ID not found in JWT claims. This should be fixed in the backend.');
    
    // For backward compatibility, fetch from database
    const userId = await getCurrentUserId();
    if (!userId) return null;
    
    const { data, error } = await supabase
      .from('user_tenants')
      .select('tenant_id')
      .eq('user_id', userId)
      .single();
      
    if (error) {
      console.error('Error getting tenant ID:', error);
      return null;
    }
    
    return data?.tenant_id || null;
  } catch (error) {
    console.error('Error getting current tenant ID:', error);
    return null;
  }
}

/**
 * Get both user ID and tenant ID in a single call
 * @returns Promise with user and tenant IDs
 */
export async function getUserAndTenantIds(): Promise<{ userId: string | null; tenantId: string | null }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { userId: null, tenantId: null };
    }
    
    // Try to get from JWT claims first (faster)
    const token = session.access_token;
    const claims = parseJwt(token);
    
    const userId = claims?.sub || session.user?.id || null;
    let tenantId = claims?.tenant_id || null;
    
    // If tenant_id is not in JWT, we'll need to fetch it (should be avoided)
    if (!tenantId && userId) {
      console.warn('Tenant ID not found in JWT claims. This should be fixed in the backend.');
      
      // For backward compatibility, fetch from database
      const { data, error } = await supabase
        .from('user_tenants')
        .select('tenant_id')
        .eq('user_id', userId)
        .single();
        
      if (!error && data) {
        tenantId = data.tenant_id;
      }
    }
    
    return { userId, tenantId };
  } catch (error) {
    console.error('Error getting user and tenant IDs:', error);
    return { userId: null, tenantId: null };
  }
}

/**
 * Get user profile picture from various possible locations in the user object
 * @param user - Supabase user object
 * @returns Profile picture URL or null if not found
 */
export function getUserProfilePicture(user: User | null): string | null {
  if (!user) return null;

  // Check user_metadata first (most common location)
  const metadata = user.user_metadata;
  if (metadata) {
    // Check common profile picture fields
    if (metadata.avatar_url) return metadata.avatar_url;
    if (metadata.picture) return metadata.picture;
    if (metadata.photo_url) return metadata.photo_url;
    if (metadata.avatar) return metadata.avatar;
    if (metadata.profile_picture) return metadata.profile_picture;
  }

  // Check identities array (for OAuth providers like Google)
  if (user.identities && user.identities.length > 0) {
    for (const identity of user.identities) {
      if (identity.identity_data) {
        const identityData = identity.identity_data;
        if (identityData.picture) return identityData.picture;
        if (identityData.avatar_url) return identityData.avatar_url;
        if (identityData.photo_url) return identityData.photo_url;
        if (identityData.avatar) return identityData.avatar;
      }
    }
  }

  // Check app_metadata as fallback
  const appMetadata = user.app_metadata;
  if (appMetadata) {
    if (appMetadata.avatar_url) return appMetadata.avatar_url;
    if (appMetadata.picture) return appMetadata.picture;
  }

  return null;
}